import React from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Typography, Paper, Chip, Breadcrumbs, Link, Grid } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

export default function DenominationPage({ coins }) {
    const { denom } = useParams();
    const navigate = useNavigate();

    // Decode params
    const decodedDenom = decodeURIComponent(denom);

    // Filter Coins for this denomination
    const filteredCoins = coins.filter(c => c.denomination === decodedDenom);

    // Group by Series
    const seriesGroups = {};
    filteredCoins.forEach(coin => {
        // Construct display string: "Dates Name"
        const s_name = coin.series_name || 'Other';
        const s_date = coin.series_date || '';
        const s = s_date ? `${s_date} ${s_name}` : s_name;

        if (!seriesGroups[s]) seriesGroups[s] = [];
        seriesGroups[s].push(coin);
    });

    const seriesList = Object.keys(seriesGroups).sort();

    // Reusing columns from SeriesTable with minor adjustments if needed
    const columns = [
        { field: 'pcgs_id', headerName: 'PCGS #', width: 90 },
        { field: 'date', headerName: 'Date', width: 90, sortable: true },
        { field: 'mint', headerName: 'Mint', width: 70 },
        { field: 'major', headerName: 'Major Variety', width: 200 },
        { field: 'type', headerName: 'Type', width: 180 },
        {
            field: 'variety',
            headerName: 'Variety / Description',
            flex: 1,
            minWidth: 200,
            valueGetter: (value, row) => value || row.major
        },
        { field: 'country', headerName: 'Country', width: 100 },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => {
                const statusVal = params.value || '';
                const isOwned = statusVal.toLowerCase().includes('bought') || statusVal.toLowerCase().includes('have');
                return (
                    <Chip
                        label={isOwned ? 'OWNED' : 'WANTED'}
                        color={isOwned ? 'success' : 'error'}
                        size="small"
                        variant={isOwned ? 'filled' : 'outlined'}
                    />
                );
            }
        },
        {
            field: 'grade',
            headerName: 'Grade',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (value, row) => {
                return value || (row.specimens && row.specimens.length > 0 ? row.specimens[0].grade : '-');
            }
        },
    ];

    return (
        <Box>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                    Home
                </Link>
                <Typography color="text.primary">{decodedDenom}</Typography>
            </Breadcrumbs>

            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    {decodedDenom}
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                    View all series within {decodedDenom} below.
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 4 }}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText', minWidth: 200 }}>
                        <Typography variant="subtitle2" sx={{ opacity: 0.8, color: 'inherit' }}>Total Purchase Price</Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: 'inherit' }}>
                            ${filteredCoins.reduce((sum, coin) => sum + (coin.specimens || []).reduce((s, spec) => s + (Number(spec.purchase_price) || 0), 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, bgcolor: 'secondary.main', color: 'secondary.contrastText', minWidth: 200 }}>
                        <Typography variant="subtitle2" sx={{ opacity: 0.8, color: 'inherit' }}>Total PCGS Value</Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: 'inherit' }}>
                            ${filteredCoins.reduce((sum, coin) => sum + (coin.specimens || []).reduce((s, spec) => s + (Number(spec.pcgs_value) || 0), 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                    </Paper>
                </Box>
            </Box>

            {seriesList.map(series => (
                <Paper key={series} sx={{ width: '100%', mb: 4, overflow: 'hidden', boxShadow: 3 }}>
                    <Box
                        onClick={() => navigate(`/series/${encodeURIComponent(decodedDenom)}/${encodeURIComponent(series)}`)}
                        sx={{
                            p: 2,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                            }
                        }}
                    >
                        <Typography variant="h6" sx={{ color: 'inherit' }}>
                            {series}
                        </Typography>
                    </Box>
                    <Box sx={{ height: 400, width: '100%' }}>
                        <DataGrid
                            rows={seriesGroups[series]}
                            columns={columns}
                            getRowId={(row) => row.pcgs_id}
                            onRowClick={(params) => navigate(`/coin/${params.row.pcgs_id}`)}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            disableSelectionOnClick={false}
                            components={{ Toolbar: GridToolbar }}
                            density="compact"
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10 } },
                            }}
                            sx={{
                                border: 0,
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f5f5f5', // Slightly different header for sub-tables if desired, or keep default
                                    color: 'inherit'
                                }
                            }}
                        />
                    </Box>
                </Paper>
            ))}

            {seriesList.length === 0 && (
                <Typography variant="h6" align="center" sx={{ mt: 4, color: 'text.secondary' }}>
                    No coins found for this denomination.
                </Typography>
            )}
        </Box>
    );
}
